import React from 'react';
import {
  ISmartSearchResult,
  SmartSearchResultType,
  SmartSearchResultSubType
} from '@Shared.Angular/@types/core/contracts/queryModel/smartSearch/smartSearchResult';
import IconButton from '@Shared.Angular/flowingly.components/buttons/IconButton';
import { StatusBadge } from '@Client/runner.library/StatusBadge';
import { ProcedureViewMode } from '@Shared.Angular/@types/core/contracts/constants/procedureViewMode';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import { EmptyGuid } from '@Shared.Angular/@types/guid';
import { getService } from '@Client/runner.hooks/useService';

type SmartSearchResultProps = {
  result: ISmartSearchResult;
};

const addSpacesToType = (type: string): string => {
  return type.replace(/([a-z])([A-Z])/g, '$1 $2');
};

const getAccuracyColorModifier = (score: number): string => {
  if (score > 80) return 'high';
  if (score > 50) return 'medium';
  return 'low';
};

const kebabCaseType = (type: string): string => {
  return type.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
};

const openView = (result: ISmartSearchResult) => {
  const $state = getService('$state');
  let url: string;
  const type = result.subType || result.type;
  switch (type) {
    case 'SOP':
      url = $state.href(STATE_MAPPINGS.procedure, {
        procedureId: result.id,
        viewMode: ProcedureViewMode.ReadOnly
      });
      window.open(window.location.origin + url, '_blank');
      break;
    case 'ProcessMap':
      url = $state.href(STATE_MAPPINGS.processmapviewv2, {
        processMapId: result.id
      });
      window.open(window.location.origin + url, '_blank');
      break;
    default:
      console.log(`No viewer available for type ${type}`);
  }
};

const openEdit = (result: ISmartSearchResult) => {
  let url: string;
  switch (result.type) {
    case 'SOP':
      url = getService('$state').href(STATE_MAPPINGS.procedure, {
        procedureId: result.id,
        viewMode: ProcedureViewMode.Edit
      });
      window.open(window.location.origin + url, '_blank');
      break;
    case 'FlowModel':
      getService('tempModelerUrlService').openModeler({
        flowModelId: result.id
      });
      break;
    default:
      console.log(`No viewer available for type ${result.type}`);
  }
};

const startWorkflow = (result: ISmartSearchResult) => {
  if (result.hasPublicForm) {
    const $state = getService('$state');
    const url = $state.href(STATE_MAPPINGS.publicForm, {
      id: result.id
    });
    window.open(window.location.origin + url, '_blank');
  } else {
    const $state = getService('$state');
    const url = $state.href(STATE_MAPPINGS.flow, {
      flowId: EmptyGuid,
      flowModelId: result.id
    });
    window.open(window.location.origin + url, '_blank');
  }
};

export const SmartSearchResult = ({ result }: SmartSearchResultProps) => {
  const displayType = result.subType || result.type;
  const showStartButton =
    result.subType === SmartSearchResultSubType.Workflow ||
    result.subType === SmartSearchResultSubType.WorkflowComponent;

  const isStartDisabled =
    result.isDraft ||
    result.subType === SmartSearchResultSubType.WorkflowComponent;

  const canView =
    (result.type === SmartSearchResultType.SOP && !result.isDraft) ||
    (result.subType === SmartSearchResultSubType.ProcessMap && !result.isDraft);

  return (
    <div
      className="smart-search-result"
      data-testid={'smart-search-result-' + result.id}
    >
      <div className="smart-search-result__header">
        <div className="smart-search-result__meta">
          <span
            className={
              'smart-search-result__type ' + kebabCaseType(displayType)
            }
          >
            {addSpacesToType(displayType)}
          </span>
          <StatusBadge status={result.status} />
        </div>
        <div className="smart-search-result__actions">
          {showStartButton && (
            <IconButton
              iconClass="fa-play"
              title={
                isStartDisabled
                  ? 'Drafts and Components cannot be started'
                  : 'Start'
              }
              disabled={isStartDisabled}
              classNames="smart-search-result__start-button"
              onClick={() => startWorkflow(result)}
            />
          )}
          {canView && (
            <IconButton
              iconClass="fa-eye"
              title="View"
              onClick={() => openView(result)}
            />
          )}
          <IconButton
            iconClass="fa-pencil"
            title={
              result.userCanEdit
                ? 'Edit'
                : 'You do not have permission to edit this'
            }
            disabled={!result.userCanEdit}
            onClick={() => openEdit(result)}
          />
        </div>
      </div>
      <div className="smart-search-result__title" title={result.name}>
        {result.name}
      </div>
      <div
        className="smart-search-result__description"
        title={result.description}
      >
        {result.description}
      </div>
      {result.reason && (
        <div className="smart-search-result__accuracy">
          {result.accuracyScore != null && (
            <div
              className="smart-search-result__accuracy-meter"
              title={`${result.accuracyScore}%`}
            >
              <div
                className={`smart-search-result__accuracy-bar smart-search-result__accuracy-bar--${getAccuracyColorModifier(
                  result.accuracyScore
                )}`}
                style={{ width: `${result.accuracyScore}%` }}
              />
            </div>
          )}
          <span className="smart-search-result__reason" title={result.reason}>
            {result.reason}
          </span>
        </div>
      )}
    </div>
  );
};
